import React, { Fragment, useState, useEffect } from 'react'
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Typography,
  CircularProgress,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox
} from '@mui/material'
import { uploadInternalOrderFile, downloadTemplate } from '../config/api'
import {
  CloudUpload as UploadIcon,
  GetApp as DownloadIcon
} from '@mui/icons-material'
import { useCourier } from '../../../hooks/useCourier'
import { useUser } from '../../../hooks/useUser'
import { useAuth } from 'oidc-react'
import { Courier, CourierService } from '../../../interfaces/Courier'
import {
  CityProvince,
  NeighborhoodComuna,
  StatesRegion
} from '../../../interfaces/Location/Location'
import { stateRegion } from '../../../utils/states-region'
import { provinces } from '../../../utils/city-provincia'
import { cities } from '../../../utils/neighborhood-comuna'
import {
  configDateInitial,
  localDateFromInput
} from '../../../utils/dateHelper'
import { OrderOriginsDDL } from '../../../components/OrderOrigin/OrderOriginsDDL'
import { templateMassiveProcessInternalOrder } from '../../../const/templatesFileName'

interface InternalOrderUploadProps {
  onSuccess: (message: string) => void
  onError: (message: string) => void
  onClose: () => void
}

interface OrderFormData {
  courierDeliveryDate: string
  courierId: number
  courierServiceId: number
  neighborhoodId: number
  stateId: number
  cityId: number
  userId: number
  rtv: boolean
  comments: string
  orderOriginId: number
}

export const InternalOrderUpload: React.FC<InternalOrderUploadProps> = ({
  onSuccess,
  onError,
  onClose
}) => {
  const maxFileSize = 2097152 // 2 MB

  const auth = useAuth()
  const isAndina = auth.userData?.profile.tenant === 'AndinaCL'

  const {
    userDataState: { userData }
  } = useUser()

  const initialFormData: OrderFormData = {
    courierDeliveryDate: configDateInitial(0),
    courierId: -1,
    courierServiceId: -1,
    neighborhoodId: !isAndina ? 1 : -1,
    stateId: !isAndina ? 1 : -1,
    cityId: !isAndina ? 1 : -1,
    userId: userData?.userId || 0,
    rtv: false,
    comments: '',
    orderOriginId: 0
  }

  const [states, setState] = useState<StatesRegion[]>([])
  const [city, setCity] = useState<CityProvince[]>([])
  const [neighborhood, setNeighborhood] = useState<NeighborhoodComuna[]>([])
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null)
  const [currentFile, setCurrentFile] = useState<File | null>(null)
  const [progress, setProgress] = useState<number>(0)
  const [disabledLink, setDisabledLink] = useState<boolean>(false)
  const [formData, setFormData] = useState<OrderFormData>(initialFormData)
  const [isRTV, setIsRTV] = useState<boolean>(initialFormData.rtv)

  const {
    couriersState: { couriers },
    getCouriers
  } = useCourier()

  useEffect(() => {
    if (couriers?.length === 0) {
      getCouriers()
    }
  }, [])

  useEffect(() => {
    setCity(provinces)
    setNeighborhood(cities)
    setState(stateRegion as [])
  }, [])

  useEffect(() => {
    setFormData({
      ...formData,
      cityId: -1,
      neighborhoodId: -1
    })
  }, [formData.stateId])

  useEffect(() => {
    setFormData({
      ...formData,
      courierServiceId: -1
    })
  }, [formData.courierId])

  useEffect(() => {
    setFormData((prevState) => ({
      ...prevState,
      rtv: isRTV
    }))
  }, [isRTV])

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [event.target.name]: event.target.value })
  }

  const handleSelectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFiles(event.target.files)
    setProgress(0)
  }

  const sizeLabel = (size: number): string => {
    if (size / 1024 / 1024 > 1) return (size / 1024 / 1024).toFixed(2) + ' MB'
    if (size / 1024 > 1) return (size / 1024).toFixed(0) + ' KB'
    return size.toFixed(2) + ' Bytes'
  }

  const validateFile = (files: FileList | null): string => {
    if (!files || files.length === 0) {
      return 'Ningún archivo seleccionado'
    }

    const selectedFile = files[0]

    if (selectedFile.size > maxFileSize) {
      const maxLabel = sizeLabel(maxFileSize)
      return `El archivo seleccionado supera el tamaño máximo: ${maxLabel}`
    }

    if (selectedFile.name.toLowerCase().indexOf('.xls') < 0) {
      return 'El archivo seleccionado no es de tipo Excel (.xls)'
    }

    return ''
  }

  const handleUpload = async () => {
    const validateError = validateFile(selectedFiles)
    if (validateError !== '') {
      setCurrentFile(null)
      onError(validateError)
      return
    }

    if (!selectedFiles) return

    const selectedFile = selectedFiles[0]
    setProgress(0)
    setCurrentFile(selectedFile)
    setIsUploading(true)

    const orderData = {
      ...formData,
      courierDeliveryDate: localDateFromInput(formData.courierDeliveryDate),
      userId: userData?.userId || 0
    }

    try {
      const response = await uploadInternalOrderFile(selectedFile, orderData)

      setIsUploading(false)

      if (response.data.success) {
        onSuccess('El archivo ha sido cargado para su procesamiento')
      } else {
        onError(
          `Error en carga de archivo: ${
            response.data.message || 'falla en el servicio.'
          }`
        )
      }

      setCurrentFile(null)
      setSelectedFiles(null)
      setProgress(0)
    } catch (error) {
      setIsUploading(false)
      setProgress(0)
      onError(
        `Error en carga de archivo: ${
          (error as any)?.data?.message || 'falla en el servicio.'
        }`
      )
      setCurrentFile(null)
      setSelectedFiles(null)
      setProgress(0)
    }
  }

  const handleDownloadTemplate = async () => {
    setDisabledLink(true)

    try {
      const response = await downloadTemplate(
        templateMassiveProcessInternalOrder
      )

      if (response.status === 200 && response.data !== null) {
        const raw = window.atob(response.data.file)
        const uInt8Array = new Uint8Array(raw.length)
        for (let i = 0; i < raw.length; ++i) {
          uInt8Array[i] = raw.charCodeAt(i)
        }
        const link = document.createElement('a')
        const blob = new Blob([uInt8Array], {
          type: response.data.mimeType
        })
        link.style.display = 'none'
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', response.data.name)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (error) {
      console.error('Error descargando plantilla:', error)
      onError('Error al descargar la plantilla')
    } finally {
      setDisabledLink(false)
    }
  }

  const courierServices = () => {
    let courierServicesList: { name: string; value: string }[] = []

    if (
      couriers !== undefined &&
      couriers.length > 0 &&
      formData.courierId !== -1 &&
      couriers.filter((f) => f.courierId === formData.courierId).length > 0
    ) {
      courierServicesList = couriers
        ?.filter((f) => f.courierId === formData.courierId)[0]
        .courierServices.map((opt: CourierService) => {
          return {
            value: `${opt.courierServiceId}`,
            name: `${opt.name}`
          }
        })
    }

    return courierServicesList
  }

  return (
    <Fragment>
      <Grid container spacing={2}>
        {isAndina && (
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label='Región'
                  id='stateId'
                  name='stateId'
                  onChange={handleFormChange}
                  variant='outlined'
                  value={formData.stateId}
                  error={formData.stateId === -1}
                  helperText={
                    formData.stateId === -1 ? 'Debe seleccionar una región' : ''
                  }
                >
                  <MenuItem key={-1} value={-1}>
                    Seleccione Región
                  </MenuItem>
                  {states &&
                    states.map((opt: StatesRegion) => (
                      <MenuItem key={opt.stateCode} value={opt.stateCode}>
                        {opt.name}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label='Provincia'
                  id='cityId'
                  name='cityId'
                  onChange={handleFormChange}
                  variant='outlined'
                  value={formData.cityId}
                  error={formData.cityId === -1}
                  helperText={
                    formData.cityId === -1
                      ? 'Debe seleccionar una provincia'
                      : ''
                  }
                >
                  <MenuItem key={-1} value={-1}>
                    Seleccione Provincia
                  </MenuItem>
                  {city &&
                    city
                      .filter((item) => item.stateId === formData.stateId)
                      .map((opt: CityProvince) => (
                        <MenuItem key={opt.cityId} value={opt.cityId}>
                          {opt.name}
                        </MenuItem>
                      ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  id='neighborhoodId'
                  name='neighborhoodId'
                  label='Comuna'
                  onChange={handleFormChange}
                  variant='outlined'
                  value={formData.neighborhoodId}
                  error={formData.neighborhoodId === -1}
                  helperText={
                    formData.neighborhoodId === -1
                      ? 'Debe seleccionar una comuna'
                      : ''
                  }
                >
                  <MenuItem key={-1} value={-1}>
                    Seleccione Comuna
                  </MenuItem>
                  {neighborhood &&
                    neighborhood
                      .filter(
                        (item: NeighborhoodComuna) =>
                          item.cityId === formData.cityId
                      )
                      .map((opt: NeighborhoodComuna) => (
                        <MenuItem
                          key={opt.neighborhoodId}
                          value={opt.neighborhoodId}
                        >
                          {opt.name}
                        </MenuItem>
                      ))}
                </TextField>
              </Grid>
            </Grid>
          </Grid>
        )}

        <Grid item xs={12}>
          <Grid container spacing={2}>
            {/* Fila 1: Canal de venta | Fecha Entrega */}
            <Grid item xs={12} sm={6}>
              <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
                Canal de venta <span style={{ color: '#d32f2f' }}>*</span>
              </Typography>
              <OrderOriginsDDL
                onChange={handleFormChange}
                value={formData.orderOriginId || 0}
                type='outlined'
                defaultLabel='Seleccione un canal de venta...'
                isSearching={false}
                fullWidth
                error={formData.orderOriginId === 0}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
                Fecha entrega courier{' '}
                <span style={{ color: '#d32f2f' }}>*</span>
              </Typography>
              <TextField
                type='date'
                fullWidth
                onChange={handleFormChange}
                id='courierDeliveryDate'
                name='courierDeliveryDate'
                variant='outlined'
                value={formData.courierDeliveryDate?.toString().split('T')[0]}
                error={formData.courierDeliveryDate.length === 0}
                helperText={
                  formData.courierDeliveryDate.length === 0
                    ? 'Debe ingresar fecha entrega courier'
                    : ''
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Fila 2: Courier | Servicio */}
            <Grid item xs={12} sm={6}>
              <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
                Courier <span style={{ color: '#d32f2f' }}>*</span>
              </Typography>
              <TextField
                select
                fullWidth
                onChange={handleFormChange}
                id='courierId'
                name='courierId'
                value={formData.courierId}
                variant='outlined'
                error={formData.courierId === -1}
                helperText={
                  formData.courierId === -1 ? 'Debe seleccionar un courier' : ''
                }
              >
                <MenuItem key={-1} value={-1}>
                  Seleccione Courier
                </MenuItem>
                {couriers &&
                  couriers.map((opt: Courier) => (
                    <MenuItem key={opt.courierId} value={opt.courierId}>
                      {opt.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
                Servicio <span style={{ color: '#d32f2f' }}>*</span>
              </Typography>
              <TextField
                select
                fullWidth
                onChange={handleFormChange}
                id='courierServiceId'
                name='courierServiceId'
                variant='outlined'
                value={formData.courierServiceId}
                error={formData.courierServiceId === -1}
                helperText={
                  formData.courierServiceId === -1
                    ? 'Debe seleccionar un servicio'
                    : ''
                }
              >
                <MenuItem key={-1} value={-1}>
                  Seleccione Servicio
                </MenuItem>
                {courierServices().map((el) => (
                  <MenuItem key={el.value} value={el.value}>
                    {el.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
                Observaciones
              </Typography>
              <TextField
                fullWidth
                name='comments'
                id='comments'
                multiline
                rows={3}
                variant='outlined'
                placeholder='Ingresa información adicional relevante (opcional)'
                onChange={handleFormChange}
                value={formData.comments}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  padding: '16px',
                  height: '100%'
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isRTV}
                      value={isRTV}
                      onChange={() => setIsRTV((prevState) => !prevState)}
                      name='chkRtv'
                      color='primary'
                    />
                  }
                  label='Pedido para RTV'
                />
                <Typography
                  variant='caption'
                  sx={{ color: 'text.secondary', display: 'block', mt: 1 }}
                >
                  Selecciona esta opción si el pedido corresponde a una RTV y
                  debe enviarse a staging de salida.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderRadius: '4px', overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                {/* Título con fondo gris */}
                <Box sx={{ backgroundColor: '#f5f5f5', padding: '12px 16px' }}>
                  <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                    Archivo de carga
                  </Typography>
                </Box>

                {/* Contenido blanco */}
                <Box sx={{ backgroundColor: 'white', padding: '16px' }}>
                  {/* Plantilla */}
                  <Box
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      padding: '12px',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={
                      disabledLink ? (
                        <CircularProgress size='1rem' />
                      ) : (
                        <DownloadIcon />
                      )
                    }
                    onClick={handleDownloadTemplate}
                    disabled={disabledLink}
                    sx={{ borderRadius: '10px', textTransform: 'none' }}
                  >
                    {disabledLink ? 'Descargando...' : 'Descargar Plantilla'}
                  </Button>
                </Box>

                {/* Seleccionar Archivo */}
                <Box
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  <label htmlFor='btn-upload-internal' style={{ margin: 0 }}>
                    <input
                      id='btn-upload-internal'
                      name='btn-upload-internal'
                      style={{ display: 'none' }}
                      type='file'
                      onChange={handleSelectFile}
                      accept='.xls,.xlsx'
                    />
                    <Button
                      variant='outlined'
                      component='span'
                      size='small'
                      startIcon={<UploadIcon />}
                      sx={{ borderRadius: '10px', textTransform: 'none' }}
                    >
                      Seleccionar Archivo
                    </Button>
                  </label>
                  <Typography
                    variant='caption'
                    sx={{ color: 'text.secondary', flex: 1 }}
                  >
                    {selectedFiles && selectedFiles.length > 0
                      ? selectedFiles[0].name
                      : 'Ningún archivo seleccionado.'}
                  </Typography>
                </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <div
        style={{
          margin: '10px',
          visibility: currentFile ? 'visible' : 'hidden'
        }}
      >
        <LinearProgress
          variant='determinate'
          value={progress}
          sx={{
            height: 15,
            borderRadius: '5px',
            backgroundColor: '#EEEEEE',
            '& .MuiLinearProgress-bar': {
              borderRadius: '5px',
              backgroundColor: '#1a90ff'
            }
          }}
        />

        <Box minWidth={35}>
          <Typography
            variant='body1'
            color='textSecondary'
            style={{ textAlign: 'end' }}
          >{`${progress}%`}</Typography>
        </Box>
      </div>

      <Box display='flex' justifyContent='flex-end' gap={2} mt={3}>
        <Button variant='outlined' color='primary' onClick={onClose}>
          Cerrar
        </Button>
        <Button
          variant='contained'
          color='primary'
          disabled={
            !selectedFiles ||
            isUploading ||
            formData.stateId === -1 ||
            formData.cityId === -1 ||
            formData.neighborhoodId === -1 ||
            formData.courierId === -1 ||
            formData.courierServiceId === -1 ||
            formData.orderOriginId <= 0
          }
          onClick={handleUpload}
        >
          Cargar
        </Button>
      </Box>
    </Fragment>
  )
}
